import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { interfaceError } from '../../i18n/errorMessages';
import toast from '../../i18n/toast';
import LocalizedMessage from '../common/LocalizedMessage';
import { displayDate } from "../common/datePresentation";
import API from "../../services/SchoolClassService";
import SettingsService from "../../services/SettingsService";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import { DEFAULT_LESSON_TYPES_SETTING, getLessonTypesSetting } from "../common/lessonTypes";
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from "../common/schoolYear";
import DashboardCalendar from "./DashboardCalendar";
import useCalendarViewPreference from "./useCalendarViewPreference";
import SelectorModal from "../common/SelectorModal";
import { canManageClasses } from './rbac';
import { useTeacherAccess } from './TeacherAccessProvider';
import { buildCreationRoute, getPrincipalKey, readCreationContext, slotToPrefill } from './dashboardClassCreationNavigation';
import {
  DEFAULT_DASHBOARD_RETURN_STATE_TTL_HOURS,
  readDashboardState,
  writeDashboardState,
} from "./dashboardStateStorage";

const isActiveTeacher = teacher => teacher && teacher.archived !== true && teacher.deleted !== true;

const compareTeachersByName = (left, right) => (
  (left.lastName || "").localeCompare(right.lastName || "", "pl", { sensitivity: "base" }) ||
  (left.firstName || "").localeCompare(right.firstName || "", "pl", { sensitivity: "base" })
);

const formatTeacherOption = teacher => ({
  value: teacher.id,
  text: `${teacher.firstName} ${teacher.lastName}`,
});

const getActiveTeachers = teachers => teachers
  .filter(isActiveTeacher)
  .sort(compareTeachersByName);

const getScheduledLessonDetails = scheduledLesson => ({
  ...(scheduledLesson.defaultLessonDetails || {}),
  ...((scheduledLesson.lessonRegister && scheduledLesson.lessonRegister.lessonDetails) || {}),
});

export function Dashboard({
  userId,
  userRole,
  userTeacherId,
  schoolId,
  activeContextKey,
  schoolClasses,
  loadSchoolClasses,
  teachers,
  loadTeachers,
  students,
  loadStudents,
  classStudents,
  loadClassStudents,
  saveLessonRegister,
  loadLessonRegisters,
  deleteLessonRegister,
  lessonRegister,
  principalKey,
  activeSchoolYearStartYear,
  onActiveSchoolYearChange,
}) {
  const { t: tr , i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { extensions } = useTeacherAccess();
  const canCreateClasses = canManageClasses(userRole) || extensions.classesManageScope !== 'none';
  const [returnContext] = useState(() => (canCreateClasses ? readCreationContext(location.state, principalKey) : null));
  const currentReturn = returnContext && returnContext.principalKey === principalKey ? returnContext : null;
  const [creationResult] = useState(currentReturn && currentReturn.result);
  const [highlightClassId, setHighlightClassId] = useState(null);
  const [creationMode, setCreationMode] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const calendarScrollTop = useRef(currentReturn ? currentReturn.returnView.calendarScrollTop : 0);
  const userInteracted = useRef(false);
  const createdClass = useRef(currentReturn && currentReturn.result ? schoolClasses.find(item => item.id === currentReturn.result.classId) : null);
  const initialClasses = useRef(schoolClasses);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const canManageDashboardClasses = userRole !== "TEACHER" || extensions.classesManageScope === 'school';
  const dashboardScope = useMemo(() => ({ accountId: userId, schoolId, contextKey: activeContextKey }), [userId, schoolId, activeContextKey]);
  const [dashboardStateTtlHours, setDashboardStateTtlHours] = useState(DEFAULT_DASHBOARD_RETURN_STATE_TTL_HOURS);
  const [restoredDashboardState, setRestoredDashboardState] = useState(() => (
    currentReturn ? { ...currentReturn.returnView, date: new Date(currentReturn.returnView.date) } : readDashboardState(dashboardScope, dashboardStateTtlHours)
  ));
  const [user, setUser] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(user);
  const { view: preferredCalendarView, selectView, loadStatus, saveStatus, retryLoad, retrySave } = useCalendarViewPreference(userId);
  const [returnViewOverride, setReturnViewOverride] = useState(currentReturn ? currentReturn.returnView.view : null);
  const calendarView = returnViewOverride || preferredCalendarView;
  const [calendarDate, setCalendarDate] = useState(restoredDashboardState?.date || new Date());
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);
  const activeTeachers = getActiveTeachers(teachers);
  const isLessonWithinActiveSchoolYear = (scheduledLesson) => {
    const lessonDate = getScheduledLessonDetails(scheduledLesson).date;

    return lessonDate >= activeSchoolYearRange.fromDate && lessonDate <= activeSchoolYearRange.tillDate;
  };

  const refreshClasses = () => {
    setLoadError(false);
    return loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(() => setLoadError(true));
  };

  useEffect(() => {
    let active = true;
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate)
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    if (!currentReturn || !currentReturn.result) return undefined;
    setHighlightClassId(currentReturn.result.classId);
    const cleanContext = { ...currentReturn };
    delete cleanContext.result;
    history.replace({ ...location, state: { ...location.state, dashboardClassCreation: cleanContext } });
    const timer = setTimeout(() => setHighlightClassId(null), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!creationMode) return undefined;
    const onEscape = (event) => { if (event.key === 'Escape') setCreationMode(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [creationMode]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (!currentReturn && !userInteracted.current) {
      const initialReturnState = readDashboardState(dashboardScope, DEFAULT_DASHBOARD_RETURN_STATE_TTL_HOURS);
      setRestoredDashboardState(initialReturnState);
      setCalendarDate(initialReturnState?.date || new Date());
    }
    try {
      SettingsService.getSettings()
        .then((settings) => {
          if (!active) return;
          const dashboardPreferences = settings.find(setting => setting.id === "dashboardPreferences");
          if (dashboardPreferences && !currentReturn && !userInteracted.current) {
            const nextTtlHours = dashboardPreferences.returnStateTtlHours;
            const nextRestoredState = readDashboardState(dashboardScope, nextTtlHours);

            setDashboardStateTtlHours(nextTtlHours);
            setRestoredDashboardState(nextRestoredState);
            setCalendarDate(nextRestoredState?.date || new Date());
          }
          setLessonTypes(getLessonTypesSetting(settings));
        })
        .catch(() => {
          if (active) setLessonTypes(DEFAULT_LESSON_TYPES_SETTING);
        });
      return () => { active = false; };
    } catch (error) {
      // Dashboard state TTL falls back to the built-in default when settings are unavailable.
    }
    return () => { active = false; };
  }, [dashboardScope]);

  useEffect(() => {
    const teacher = userRole === "TEACHER" && userTeacherId
      ? teachers.find(t => t.id === userTeacherId) || teachers.find(t => t.email === userId)
      : teachers.find(t => t.email === userId);
    const restoredTeacher = canManageDashboardClasses && restoredDashboardState && restoredDashboardState.teacherId
      ? getActiveTeachers(teachers).find(t => t.id === restoredDashboardState.teacherId)
      : null;
    const teacherToSelect = restoredTeacher || teacher;

    if (teacherToSelect && !userInteracted.current) {
      setSelectedTeacher({ value: teacherToSelect.id, text: `${teacherToSelect.firstName} ${teacherToSelect.lastName}` });
    } else {
      setSelectedTeacher({});
    }

    if (teacher) {
      setUser(teacher);
    }
  }, [canManageDashboardClasses, restoredDashboardState, teachers, userId, userRole, userTeacherId]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (students.length === 0) {
      loadStudents().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, []);

  useEffect(() => {
    const updateScheduledClasses = () => {
      if (createdClass.current && schoolClasses !== initialClasses.current && schoolClasses.some(item => item.id === createdClass.current.id)) {
        createdClass.current = null;
      }
      const protectedClass = currentReturn && createdClass.current;
      const availableClasses = protectedClass && !schoolClasses.some(item => item.id === protectedClass.id)
        ? [...schoolClasses, protectedClass] : schoolClasses;
      const filteredClasses = availableClasses.filter((schoolClass) => {
        const classFrom = schoolClass.fromDate;
        const classTill = schoolClass.tillDate || schoolClass.fromDate;

        if (classTill < activeSchoolYearRange.fromDate) return false;
        if (classFrom > activeSchoolYearRange.tillDate) return false;

        return true;
      });

      const generatedClasses = filteredClasses.map((schoolClass) => {
        const scheduledLessons = API.generateScheduledClasses(
          schoolClass,
          lessonRegister,
          students,
          classStudents,
        )
          .filter(isLessonWithinActiveSchoolYear)
          .filter(scheduledLesson => getScheduledLessonDetails(scheduledLesson).teacherId === selectedTeacher.value);

        return {
          schoolClassId: schoolClass.id,
          scheduledLessons,
        };
      }).filter(scheduledClass => scheduledClass.scheduledLessons.length > 0);
      setScheduledClasses(generatedClasses);
    };

    updateScheduledClasses();
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    classStudents,
    lessonRegister,
    schoolClasses,
    selectedTeacher,
    students,
  ]);

  const handleTeacherSelect = (option) => {
    userInteracted.current = true;
    setCreationMode(false);
    setSelectedTeacher(option);
    setIsModalOpen(false);
    writeDashboardState(dashboardScope, { teacherId: option.value, date: calendarDate });
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
      });
  };

  const openCreationForm = (slot) => {
    if (!canCreateClasses || !creationMode || !activeTeachers.some(teacher => teacher.id === selectedTeacher.value)) return;
    if (userRole === 'TEACHER' && extensions.classesManageScope === 'own' && selectedTeacher.value !== userTeacherId) return;
    const context = {
      version: 1,
      principalKey,
      returnView: {
        teacherId: selectedTeacher.value,
        date: calendarDate.toISOString(),
        view: calendarView,
        schoolYearStartYear: activeSchoolYearStartYear,
        pageScrollY: window.scrollY || 0,
        calendarScrollTop: calendarScrollTop.current,
      },
      prefill: slot ? slotToPrefill(slot.start, calendarView, selectedTeacher.value) : { teacherId: selectedTeacher.value },
    };
    setCreationMode(false);
    history.replace({ ...location, state: { ...location.state, dashboardClassCreation: context } });
    history.push(buildCreationRoute(context, location.search));
  };

  const handleVisibleDateChange = (visibleDate) => {
    userInteracted.current = true;
    setCreationMode(false);
    setCalendarDate(visibleDate);
    writeDashboardState(dashboardScope, { teacherId: selectedTeacher.value, date: visibleDate });
    const nextSchoolYearStartYear = getCurrentSchoolYearStartYear(visibleDate);

    if (nextSchoolYearStartYear !== activeSchoolYearStartYear) {
      onActiveSchoolYearChange(nextSchoolYearStartYear);
    }
  };

  const handleViewChange = (view) => {
    userInteracted.current = true;
    setCreationMode(false);
    setReturnViewOverride(null);
    selectView(view);
    writeDashboardState(dashboardScope, { teacherId: selectedTeacher.value, date: calendarDate });
  };

  const handleDashboardActivity = () => {
    userInteracted.current = true;
    writeDashboardState(dashboardScope, { teacherId: selectedTeacher.value, date: calendarDate });
  };

  return (
    <div
      className="mb-5"
      onClickCapture={handleDashboardActivity}
      onKeyDownCapture={handleDashboardActivity}
      role="presentation"
    >
      {(canManageDashboardClasses || canCreateClasses) && (
      <h4 className="col-block d-flex flex-wrap align-items-center mb-3">{tr("manage:viewing_172")}  {selectedTeacher.text || `${userId}`}
        {canManageDashboardClasses && (
        <button
          type="button"
          className="manage-select-button"
          onClick={() => setIsModalOpen(true)}
        >{tr("manage:selectATeacher_173")}</button>
        )}
        {canCreateClasses && !creationMode && (
          <button
            type="button"
            className="btn manage-button manage-red-button ml-auto"
            disabled={!activeTeachers.some(teacher => teacher.id === selectedTeacher.value) ||
              (userRole === 'TEACHER' && extensions.classesManageScope === 'own' && selectedTeacher.value !== userTeacherId)}
            onClick={() => setCreationMode(true)}
            title={selectedTeacher.value ? tr("manage:addClass_174") : tr("manage:selectATeacher_173")}
          >{tr("manage:addClass_175")}</button>
        )}
      </h4>
      )}
      {canManageDashboardClasses && (
      <SelectorModal
        title={tr("manage:selectATeacher_173")}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        values={activeTeachers.map(formatTeacherOption)}
        onSelect={option => handleTeacherSelect(option)}
        selectedIDs={[selectedTeacher.value || user.id]}
        layout="grid"
      />
      )}
      {creationMode && (
        <div className="alert alert-info dashboard-creation-toolbar">
          <span role="status">{tr("manage:chooseAnAvailableSlotInTheTimetable_176")}</span>
          <button type="button" className="btn manage-button manage-teal-button ml-2" onClick={() => setCreationMode(false)}>{tr("common:cancel")}</button>
          <button type="button" className="btn manage-button manage-teal-button ml-2" onClick={() => openCreationForm(null)}>{tr("manage:enterTheTimeInTheForm_177")}</button>
        </div>
      )}
      {currentReturn && creationResult && (
        <div role="status" className="alert alert-success">{tr("manage:classCreated_178")} {displayDate(creationResult.fromDate, i18n.language)} {creationResult.startTime}
          {' — '}{teachers.filter(teacher => teacher.id === creationResult.teacherId).map(teacher => `${teacher.firstName} ${teacher.lastName}`).join('')}
        </div>
      )}
      {loadError && (
        <div role="alert" className="alert alert-danger">{tr("manage:couldNotRefreshTheTimetable_179")} <button type="button" className="btn manage-teal-button" onClick={refreshClasses}>{tr("manage:refreshTimetable_180")}</button>
        </div>
      )}
      {errors.saveError && <div role="alert" className="alert alert-danger"><LocalizedMessage isError value={errors.saveError} /></div>}
      {(loadStatus === "error" || saveStatus === "error") && (
        <div className="alert alert-warning" role="alert">
          {saveStatus === "error" ? tr("manage:couldNotSaveTheView_181") : tr("manage:couldNotLoadTheSavedView_182")}
          {" "}
          <button type="button" className="manage-teal-button" onClick={saveStatus === "error" ? retrySave : retryLoad}>{tr("manage:tryAgain_183")}</button>
        </div>
      )}
      <DashboardCalendar
        creationMode={creationMode && canCreateClasses}
        onSelectCreationSlot={openCreationForm}
        onCalendarScrollChange={scrollTop => { calendarScrollTop.current = scrollTop; }}
        restoreScrollTop={currentReturn ? currentReturn.returnView.calendarScrollTop : null}
        restorePageScrollY={currentReturn ? currentReturn.returnView.pageScrollY : null}
        highlightClassId={highlightClassId}
        scheduledClasses={scheduledClasses}
        teachers={teachers}
        students={students}
        onSubmit={handleRegisterUpdate}
        deleteLessonRegister={deleteLessonRegister}
        view={calendarView}
        date={calendarDate}
        onViewChange={handleViewChange}
        onVisibleDateChange={handleVisibleDateChange}
        userRole={userRole}
        userTeacherId={userTeacherId}
        lessonTypes={lessonTypes}
      />
    </div>
  );
}

Dashboard.propTypes = {
  userId: PropTypes.string.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  schoolId: PropTypes.string,
  activeContextKey: PropTypes.string,
  principalKey: PropTypes.string,
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  classStudents: PropTypes.array.isRequired,
  lessonRegister: PropTypes.array.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  onActiveSchoolYearChange: PropTypes.func.isRequired,
};

Dashboard.defaultProps = { userRole: '', userTeacherId: '', principalKey: '', schoolId: '', activeContextKey: '' };

const mapStateToProps = state => ({
  schoolId: state.user.schoolId,
  activeContextKey: state.user.activeContextKey,
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  principalKey: getPrincipalKey(state.user),
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
